'use client';

import { Bolt } from '@velocity-exchange/icons';
import XButton from 'src/components/Buttons/XButton';
import useModal from 'src/components/Modals/hooks/useModal';
import Button from 'src/components/primitives/Button/Button';
import { PageRoute } from 'src/constants/constants';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useAccountExists from 'src/hooks/useAccountExists';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useGoToRoute from 'src/hooks/useGoToRoute';
import useReferrerNameAccounts from 'src/hooks/useReferrerNameAccounts';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { cm } from 'src/utils/classNames';
import { getColorVar } from 'src/utils/theme/colorVars';
import { WithClassName } from 'src/utils/UtilityTypes';

export const PRIVATE_BETA_PERKS_DISMISSED_KEY = 'private_beta_perks_dismissed';

const perkChipClassName = cm(
	'h-full w-full flex-col items-start justify-start gap-y-0',
	'rounded-sm p-sm text-left whitespace-normal',
	'transition-transform duration-200 ease-out-smooth hover:z-1 hover:scale-[1.03]'
);

export default function PrivateBetaPerksCard({ className }: WithClassName) {
	const { show } = useModal();
	const goToRoute = useGoToRoute();
	const isWalletConnected = useWalletIsConnected();
	const accountExists = useAccountExists();
	const { referrerNameAccounts } = useReferrerNameAccounts();
	const [hasDismissed, setHasDismissed] = useSyncLocalStorage(
		PRIVATE_BETA_PERKS_DISMISSED_KEY,
		false
	);

	if (!Env.enablePrivateBetaPerks || hasDismissed) {
		return null;
	}

	const perksLabel = Env.enableOpenBeta
		? 'Beta Testers perks'
		: 'Private Beta perks';

	const handleReferralPerkClick = () => {
		// No wallet or no account yet: nothing to create a link with, so just
		// show the referrals page
		if (!isWalletConnected || !accountExists) {
			goToRoute(`/${PageRoute.referrals}`);
			return;
		}

		if (!referrerNameAccounts.initialReferrerNameAccountsLoaded) {
			return;
		}

		if (referrerNameAccounts.accounts.length > 0) {
			goToRoute(`/${PageRoute.referrals}`);
			return;
		}

		show({ type: 'create_referral_link', props: {} });
	};

	return (
		<section
			className={cm(
				'flex flex-col gap-y-md rounded-md p-md',
				// Same wash as accelerated-referral chrome: gold owns this region
				// so the card lifts off the surrounding container-bg panels.
				'bg-container-bg bg-[linear-gradient(165deg,var(--star-yellow-bg)_0%,transparent_72%)]',
				className
			)}
			aria-label={perksLabel}
		>
			<div className="flex items-start justify-between gap-x-sm">
				<p className="flex items-center gap-x-xs typo-label-md text-text-default">
					<Bolt size={16} color={getColorVar('star-yellow')} />
					{perksLabel}
				</p>
				<XButton
					size="sm"
					aria-label={`Dismiss ${perksLabel}`}
					onClick={() => setHasDismissed(true)}
				/>
			</div>

			<div className="grid grid-cols-2 gap-x-sm overflow-visible">
				<Button
					size="sm"
					type="button"
					className={cm(
						perkChipClassName,
						'bg-star-yellow-bg hover:bg-star-yellow-bg'
					)}
					onClick={() => show({ type: 'fee_tier', props: {} })}
				>
					<span className="typo-label-md text-text-default dark:text-star-yellow">
						2 bps fees
					</span>
					<span className="typo-body-2xs text-text-secondary dark:text-star-yellow">
						limited time
					</span>
				</Button>
				<Button
					size="sm"
					type="button"
					className={cm(
						perkChipClassName,
						'bg-positive-green-secondary-bg hover:bg-positive-green-secondary-bg'
					)}
					onClick={handleReferralPerkClick}
				>
					<span className="typo-label-md text-text-default dark:text-text-positive-green-button">
						20% bonus
					</span>
					<span className="typo-body-2xs text-text-secondary dark:text-text-positive-green-button">
						referral, forever
					</span>
				</Button>
			</div>
		</section>
	);
}
